import { useState, useEffect} from 'react';
import React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import './chartDisplay.css';
import Box from '@mui/material/Box';
import { Button, capitalize } from '@mui/material';

import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton'; 
import ListItemText from '@mui/material/ListItemText';
import { useHCF, useRockbash, useRenewableForecast, useShortTermForecast, useHistoricalForecast, useRevenue, useWeeklyPeakLoadForecast, useRevenueMonthly, useRockbashAccuracy, useRockbashAccuracyTimeSeries, useMarginalCost,useBidData} from '../api/GetData';

function rollingAverage(data, windowSize = 7) {
    const result = [];
    for (let i = 0; i < data.length; i++) {
        // Slice safely: if i < windowSize, take only the available points
        const start = Math.max(0, i - windowSize + 1);
        const window = data.slice(start, i + 1);
        const avg = window.reduce((sum, val) => sum + val, 0) / window.length;
        result.push(avg);
    }
    return result;
}

function getCurrentHourPower(HCFdata) {
    if (!HCFdata) return [];

    const categories = Object.keys(HCFdata).filter(
        category => HCFdata[category] && HCFdata[category].length > 0
    );

    const now = new Date();

    const pieChartData = categories.map(category => {
        const dataPoints = HCFdata[category] || [];

        const currentPoint = dataPoints.find(point => {
        const pointDate = new Date(point.datetime);
        return (
            pointDate.getUTCFullYear() === now.getUTCFullYear() &&
            pointDate.getUTCMonth() === now.getUTCMonth() &&
            pointDate.getUTCDate() === now.getUTCDate() &&
            pointDate.getUTCHours() === now.getUTCHours()
        );
        });

        const powerValue = currentPoint ? Math.round(parseFloat(currentPoint.power)) : 0;

        return {
        label: category,
        value: powerValue
        };
    }).filter(item => item.value > 0);  // Optional: remove slices with 0

    return pieChartData;
    }

function toChartNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function pickFirstChartNumber(row, keys = []) {
    for (const key of keys) {
        const value = toChartNumber(row?.[key]);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

function buildOptionalSeries(rows, configs, showMark) {
    if (!Array.isArray(rows)) return [];
    return configs
        .filter((config) =>
            rows.some((row) => pickFirstChartNumber(row, config.keys) !== null)
        )
        .map((config) => ({
            data: rows.map((row) => pickFirstChartNumber(row, config.keys)),
            label: config.label,
            color: config.color,
            showMark,
        }));
}

function buildPoolPriceModelSeries(rows, showMark) {
    return buildOptionalSeries(
        rows,
        [
            {
                keys: ['forecast_price'],
                label: 'WISDM Forecast Price ($)',
                color: '#545454',
            },
            {
                keys: ['extra_trees_refined_forecast_price'],
                label: 'Extra Trees Refined ($)',
                color: '#2f855a',
            },
            {
                keys: ['rf_refined_forecast_price'],
                label: 'RF Refined ($)',
                color: '#7c3aed',
            },
            {
                keys: ['zoo_extra_trees_forecast_price'],
                label: 'Zoo Extra Trees ($)',
                color: '#14b8a6',
            },
            {
                keys: ['zoo_rf_forecast_price'],
                label: 'Zoo RF ($)',
                color: '#f59e0b',
            },
        ],
        showMark
    );
}

function buildRockbashModelSeries(rows, showMark) {
    return buildOptionalSeries(
        rows,
        [
            {
                keys: ['forecastplus_price'],
                label: 'WISDM Forecast',
                color: '#545454',
            },
            {
                keys: ['aeso_forecast_price'],
                label: 'AESO Forecast',
                color: '#3c8dde',
            },
            {
                keys: ['extra_trees_refined_forecast_price'],
                label: 'Extra Trees Refined',
                color: '#2f855a',
            },
            {
                keys: ['rf_refined_forecast_price'],
                label: 'RF Refined',
                color: '#7c3aed',
            },
            {
                keys: ['zoo_extra_trees_forecast_price', 'forecast_price'],
                label: 'Zoo Extra Trees',
                color: '#f59e0b',
            },
            {
                keys: ['zoo_rf_forecast_price'],
                label: 'Zoo RF',
                color: '#d97706',
            },
            {
                keys: ['forecast'],
                label: 'H10 Model',
                color: '#4681E1',
            },
        ],
        showMark
    );
}

export const ChartSelectorDisplay = ({set=[0,0], selectedCharts, pageKey='CustomerPersona', plantName}) => {
    const List1 = ["Current Power Generation","Forecasted Demand"];
    const List2 = ["Current Supply","Long Term Supply"];
    const {data: stfdata} = useShortTermForecast();
    const {data: hdata} = useHistoricalForecast();
    const {data: rdata} = useRevenue(plantName);
    const {data: wdata} = useWeeklyPeakLoadForecast();
    const {data: rmdata} = useRevenueMonthly();
    const {data: rbdata} = useRockbash();
    const {data: renewabledata} = useRenewableForecast();
    const {data: marginalCost} = useMarginalCost(plantName);
    const {data: HCFdata} = useHCF()
    const {data:biddata} = useBidData(plantName)
    const { data: accuracyData, isLoading: accuracyLoading, error: accuracyError } = useRockbashAccuracyTimeSeries();
    const [anchorEl1, setAnchorEl1] = useState(null);
    const [anchorEl2, setAnchorEl2] = useState(null);
    const [selectedItem1, setSelectedItem1] = useState(set[0]);
    const [selectedItem2, setSelectedItem2] = useState(set[1]);
    const [name1, setName1] = useState(List1[set[0]]);
    const [name2, setName2] = useState(List2[set[1]]);
    const [accuracyHistogram, setAccuracyHistogram] = useState([]);
    const [accuracy, setAccuracy] = useState([]);
    const [bidAccuracy, setBidAccuracy] = useState([]);
    // Placeholder 24-hour values (for now same as above)
    const[datetimeAccuracy, setDatetimeAccuracy] = useState([]);
    const [accuracy24h, setAccuracy24h] = useState([]);
    const [bidAccuracy24h, setBidAccuracy24h] = useState([]);
    const { isPending, isError, data: timeSeriesAccuracyData, error } = useRockbashAccuracy();
    const [chartData, setChartData] = useState([]);
    const plantLabel = plantName ? String(plantName) : "WLC1";
    const plantLabelUpper = plantLabel.toUpperCase() === "WLC1" ? "WLC1" : plantLabel;
    const parseRevenueData = (raw) => {
        if (!raw) return null;
        if (Array.isArray(raw)) return raw;
        if (typeof raw === "string") {
            try {
                return JSON.parse(raw);
            } catch (error) {
                console.error("Failed to parse revenue data:", error);
                return null;
            }
        }
        return null;
    };
    const revenueData = parseRevenueData(rdata);
    const rollingAccuracy = rollingAverage([...accuracy24h].reverse(), 7);
    const rollingBidAccuracy = rollingAverage([...bidAccuracy24h].reverse(), 7);
    const parseBidData = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === "string") {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
                if (Array.isArray(parsed?.BidData)) return parsed.BidData;
            } catch (error) {
                console.error("Failed to parse bid data:", error);
            }
            return [];
        }
        if (typeof raw === "object") {
            if (Array.isArray(raw.body)) return raw.body;
            if (typeof raw.body === "string") {
                try {
                    const parsed = JSON.parse(raw.body);
                    if (Array.isArray(parsed)) return parsed;
                    if (Array.isArray(parsed?.BidData)) return parsed.BidData;
                } catch (error) {
                    console.error("Failed to parse bid data body:", error);
                }
            }
            if (Array.isArray(raw.BidData)) return raw.BidData;
            if (Array.isArray(raw.data)) return raw.data;
        }
        return [];
    };
    const bidPoints = parseBidData(biddata);
    const bidChartPoints = bidPoints
        .map((item) => {
            let timeValue = null;
            let dataValue = null;
            if (Array.isArray(item)) {
                timeValue = item[0];
                dataValue = item[3];
            } else if (item && typeof item === "object") {
                timeValue = item.datetime ?? item.timestamp ?? item.time ?? item.date;
                dataValue = item.expected_dispatch ?? item.expectedDispatch ?? item.dispatch ?? item.mw ?? item.value ?? item.offer ?? item.quantity;
            }
            const time = new Date(timeValue ?? 0);
            const value = Number(dataValue ?? 0);
            if (Number.isNaN(time.getTime()) || Number.isNaN(value)) {
                return null;
            }
            return { time, value };
        })
        .filter(Boolean);
    const buildBidFallbackPoints = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return [
            { time: today, value: 0 },
            { time: tomorrow, value: 0 },
        ];
    };
    const bidChartSeriesPoints = bidChartPoints.length > 0
        ? bidChartPoints
        : buildBidFallbackPoints();
    const pieChartData = getCurrentHourPower(HCFdata);
    
    const defaultCharts = {
        short_forecast: true,
        pool_price: true,
        accuracy_histogram: true,
        generation: true,
        sevenD_accuracy: true,
        quantify_beta: true,
        revenue: true,
        ST_supply: true,
        LT_rockbash: true,
        LT_supply: true,
        total_generation: true,
        gross_profit: true,
        market_offers_optimized:true,
    }
    const [newselectedCharts, setSelectedCharts] = useState(defaultCharts);
    useEffect(() => {
    const saved = localStorage.getItem(pageKey);
    if (saved) setSelectedCharts(JSON.parse(saved));
    }, [pageKey]);

      // Toggle chart and save to localStorage
      const toggleChart = (chartName) => {
        setSelectedCharts((prev) => {
          const updated = { ...prev, [chartName]: !prev[chartName] };
          localStorage.setItem(pageKey, JSON.stringify(updated));
          return updated;
        });
      };
 // Pie Chart Formatting
    const categories = HCFdata 
    ? Object.keys(HCFdata).filter(
        category => HCFdata[category] && HCFdata[category].length > 0
        )
    : [];
    const getColorForCategory = (category) => { // the colors for each of the categories in the hourly capability chart
        const colors = {
          'CC': '#212121',
          'GFS': '#4681E1',
          'ENERGY STORAGE': '#545454',
          'HYDRO': '#6978A2',
          'WIND': '#3a3a3a',
          'SOLAR': '#2E8BC0',
          'SC': '#011f4b',
          'Cogen': '#6497b1'
          
        };
        return colors[category] || '#5dd0ff'; // Default color if category not found
    }
    const getCategorySeries = (category) => {
        
        const data = HCFdata !== null ? HCFdata[category]: [];
        if (data && data.length > 0) {
          return {
            id: category.toLowerCase().replace(/\s/g, '-'),
            label: category.replace(/_/g, ' '),
            labelStyle: {
              fontSize: '15px',
              fontFamily: 'Tahoma',
              letterSpacing: '0.1em',
              color: getColorForCategory(category), // Function to get color based on category
            },
            data: data.map(value => parseInt(value['power'])),
            stack: 'total',
            area: true,
            showMark: false,
            color: getColorForCategory(category), // Function to get color based on category
          };
        }
        return null;
      };
    const longestCategory =
    HCFdata && categories?.length > 0
        ? categories.reduce((a, b) =>
            (HCFdata[a]?.length || 0) > (HCFdata[b]?.length || 0) ? a : b
        )
        : null;

    const xAxisData =
    longestCategory && HCFdata
        ? (HCFdata[longestCategory] || [])
            .map(d => (d && d.datetime ? new Date(d.datetime) : null))
            .filter(d => d !== null)
        : [];
    const series = categories !== null ? categories.map(getCategorySeries).filter(series => series !== null) : null;

    useEffect(() => {
        if (accuracyData && Array.isArray(accuracyData)) {
            const bins = Array(15).fill(0);
            accuracyData.forEach(item => {
                if (item.accuracy !== undefined) {
                    const idx = Math.min(14, Math.floor(item.accuracy / 20));
                    bins[idx]++;
                }
            });
            setAccuracyHistogram(bins.map((count, i) => {
                const lower = i * 20;
                const upper = (i + 1) * 20;
                return { range: i === 14 ? `${lower}%+` : `${lower}-${upper}%`, count };
            }));
        }
        console.log('accuracyData:', accuracyData);
    }, [accuracyData]);

    
useEffect(() => {
    if (timeSeriesAccuracyData && Array.isArray(timeSeriesAccuracyData)) {
        console.log('We got in the IF', timeSeriesAccuracyData);
        // Ensure every item is defined and has a datetime
        const validData = timeSeriesAccuracyData.filter(item => item && item.datetime);
        console.log('valid data:', validData);
        const newDatetimeAccuracy = validData.map(item => new Date(item.datetime))
        const new24hAvgAccuracy = validData.map(item => item.avg_accuracy_24h ?? null)
        const new24hAvgAccuracyBid = validData.map(item => item.bid_accuracy_true_24h ?? null)
        setDatetimeAccuracy((prev) => {
            let list = [...prev];
            list = newDatetimeAccuracy;
            return list;
        });
        setAccuracy24h(new24hAvgAccuracy);
        setBidAccuracy24h(new24hAvgAccuracyBid);
        console.log('datetimeAccuracy array:', newDatetimeAccuracy);
        console.log('24hAccuracy array:', new24hAvgAccuracy);
        console.log('24hAccuracyBid array:', new24hAvgAccuracyBid);
        
    }
}, [timeSeriesAccuracyData]);


useEffect(()=>{ //why aren't we just using rmdata why use a state variable
    const plantFilter = (entry) => {
      if (!plantName) return true;
      const asset = String(entry?.asset ?? "").toLowerCase();
      const plant = String(plantName).toLowerCase();
      return asset.includes(plant);
    };
    setChartData(Array.isArray(rmdata) ? rmdata.filter(plantFilter) : rmdata)
    console.log('rmdata', rmdata);
    console.log('MC', marginalCost);
    console.log('renewable data', renewabledata);
    console.log('rockbash:', rbdata);
},[rmdata, plantName])

    ?.filter(entry => entry && entry.datetime && entry.wisdm_revenue)
    .map(entry => ({
    datetime: new Date(entry.datetime),
    wisdm_revenue: parseFloat(entry.wisdm_revenue),
    customer_revenue: parseFloat(entry.customer_revenue),
    asset: entry.asset
  }))
  .sort((a, b) => a.datetime - b.datetime); // Ensure chronological order

    
    const handleClick1 = (event, item) => {
        setAnchorEl1(event.currentTarget);  
    };
    const handleClick3 = (event, item) => {
        setSelectedItem1(item);   
        setAnchorEl1(null); 
        if(item === 0){
            setName1("Current Power Generation")
        }else{
            setName1("Forecasted Demand")
        }
    };
    
    const handleClose1 = () => {
        setAnchorEl1(null);
    };

    const handleClick2 = (event, item) => {
        setAnchorEl2(event.currentTarget);
    };
    const handleClick4 = (event, item) => {
        setSelectedItem2(item);   
        setAnchorEl2(null); 
        if(item === 0){
            setName2("Current Supply")
        }else{
            setName2("Long Term Supply")
        }
    };
    const handleClose2 = () => {
        setAnchorEl2(null);
    };
    const open2 = Boolean(anchorEl2);
    const id2 = open2 ? 'simple-popover-2' : undefined;
    const open1 = Boolean(anchorEl1);
    const id1 = open1 ? 'simple-popover-1' : undefined;

    const demandSeries = Array.isArray(wdata)
      ? [...wdata].sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
      : [];



    return(
            <Box sx={{    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(clamp(360px, 42vw, 700px), 1fr))",
    gridTemplateRows: 'repeat(30, minmax(50px, auto))',
    gap: 2,
    alignItems: 'start',
    width: "100%"}}>
            {selectedCharts.short_forecast && (
                <Box className='graph'> {/*Small Forecast*/}
                <p className='Title'>Short Term Forecast</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80}}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                        fontSize: '10px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                        padding: 5,
                    },

                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    }
                }}
                xAxis={[{
                    id: 'date',
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    scaleType: 'time',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: stfdata !== undefined ? stfdata.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: stfdata !== undefined ? new Date(stfdata[0]['datetime']) : new Date(0), // Start date
                    max: stfdata !== undefined ? new Date(stfdata[stfdata.length -  1]['datetime']) : new Date(0), // End date
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{ 
                    label:'Pool Price (CAD $)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                }]}
                series={[  
                    {
                    data: stfdata !== undefined ? stfdata.map((xValue, index) => {
                        if(xValue['pool_price']!==null){
                            return Number(parseFloat(xValue['pool_price']))
                        }else{
                            return null
                        }
                        
                    }):[],
                    label: 'Pool Price ($)',
                    color: '#de3c64', 
                    showMark: true,
                },
                ...buildPoolPriceModelSeries(stfdata, true),
                {
                    data: stfdata !== undefined ? stfdata.map((xValue, index) => {
                        if(xValue['aeso_forecast_price'] !== null){
                            return Number(parseFloat(xValue['aeso_forecast_price']))
                        }else{
                            return null
                        }
                        
                    }):[],
                    label: 'AESO Forecast Price ($)',
                    color: '#3c8dde', 
                    showMark: true,
                },
                ]}
                />
            </Box>  
            </Box>
            )}
            {selectedCharts.pool_price && (
            <Box className='graph'> {/*Pool Price*/}
                <p className='Title'>Pool Price</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 1,
                        labelStyle: {
                            fontSize: '15px',
                            letterSpacing: '0.1em',
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                }}}
                xAxis={[{
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: hdata !== undefined ? hdata.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: hdata !== undefined ? new Date(hdata[0]['datetime']) : new Date(0), // Start date
                    max: hdata !== undefined ? new Date(hdata[hdata.length -  1]['datetime']) : new Date(0), // End date
                    scaleType: 'time',
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{ 
                    label:'Canadian Dollars ($)\n\n',
                    labelStyle: {
                    fontWeight: 'Bold',
                    fontSize: '15px',
                    fontFamily: 'Tahoma', 
                    letterSpacing: '0.1em',
                    }
                }]}
                series={[{
                    data: hdata !== undefined ? hdata.map((xValue, index) => {
                        return Number(parseFloat(xValue['pool_price']))
                    }):[],
                    label: 'Pool Price ($)',
                    color: '#de3c64', 
                    showMark: false,
                },
                {
                    data: hdata !== undefined ? hdata.map((xValue, index) => {
                        return Number(parseFloat(xValue['aeso_forecast_price']))
                    }):[],
                    label: 'AESO Forecast Price ($)',
                    color: '#3c8dde', 
                    showMark: false,
                },
                ...buildPoolPriceModelSeries(hdata, false),
            ]}
                />
            </Box>
            </Box>
            )}
            {selectedCharts.accuracy_histogram && (
            <Box className='graph'>
                <p className='Title'>Accuracy Distribution</p>
                <Box className='back'>
                    {(accuracyHistogram.length === 0) ? (
                        <p>No accuracy data available</p>
                    ) : (
                        <BarChart
                        margin={{ left: 80 }}
                        slotProps={{
                            legend: {
                                direction: 'row',
                                position: { vertical: 'top', horizontal: 'middle' },
                                padding: 1,
                                labelStyle: {
                                    fontSize: '15px',
                                    letterSpacing: '0.1em',
                                },
                                itemMarkWidth: 10,
                                itemMarkHeight: 10,
                        }}}
                            xAxis={[{
                                label: 'Accuracy Range (%)',
                                labelStyle: {
                                    fontWeight: 'Bold',
                                    fontSize: '15px',
                                    fontFamily: 'Tahoma', 
                                    letterSpacing: '0.1em',
                                    },
                                data: accuracyHistogram.map(item => item.range),
                                scaleType: 'band',
                            }]}
                            yAxis={[{
                                labelStyle: {
                                    fontWeight: 'Bold',
                                    fontSize: '15px',
                                    fontFamily: 'Tahoma', 
                                    letterSpacing: '0.1em',
                                    },
                                label: 'Frequency',
                            }]}
                            series={[{
                                data: accuracyHistogram.map(item => item.count),
                                label: 'Count',
                                color: '#4681E1',
                            }]}
                        />
                    )}
                </Box>
            </Box>
            )}
            {selectedCharts.generation && (
            <Box className='graph'> {/*Generation*/}
                <p className='Title'>{name1}</p>
                <div>
                    <Button className='selectBox' aria-describedby={id1} variant="contained" onClick={(e) => handleClick1(e, 'Option 1')}>▼</Button>
                    <Popover
                        id={id1}
                        open={open1}
                        anchorEl={anchorEl1}
                        onClose={handleClose1}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <List component="div" aria-label="simple dropdown1">
                            {List1.map((option, index) => (
                                <ListItemButton key={index} onClick={(e) => handleClick3(e, index)}>
                                    <ListItemText primary={option} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Popover>
                </div>
                <Box className='back'>
                {selectedItem1 === 0 ? (
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    }
                }}
                xAxis={[{
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: Array.isArray(revenueData) ? revenueData.map(dateString => {
                        if (dateString['datetime']) {
                            const date = new Date(dateString['datetime']);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: Array.isArray(revenueData) && revenueData.length > 0 ? new Date(revenueData[0]['datetime']) : new Date(0), // Start date
                    max: Array.isArray(revenueData) && revenueData.length > 0 ? new Date(revenueData[revenueData.length -  1]['datetime']) : new Date(0), // End date
                    disableTicks: true,
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{ 
                    label:'Generation (MWH)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    }
                }]}
                series={[{
                    data: Array.isArray(revenueData) ? revenueData.map((xValue, index) => {
                    return Number(parseFloat(xValue['TNG']))
                    }):[],
                        label: `Generation ${plantLabelUpper} (MWH)`,
                    color: '#4681E1', //dark green
                    showMark: true,
                    area: true
                }]}
                /> 
            ):(
                <LineChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 0,
                            labelStyle: {
                                fontSize: '15px',
                                fontFamily: 'Tahoma',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        },
                    }}
                      
                      yAxis={[{ 
                        label:'Demand (MW)\n\n',
                        labelStyle: {
                            fontWeight: 'bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                      }]}
                      xAxis={[{
                        label: 'Datetime (YYYY-MM-DD HH:mm)',
                        labelStyle: {
                            fontWeight: 'bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma', // Example of adding fontFamily
                            letterSpacing: '0.1em',
                        },
                        scaleType: 'time',
                        data: demandSeries.map((dateString) => new Date(dateString.datetime)),
                        min: demandSeries.length > 0 ? new Date(demandSeries[0].datetime) : new Date(0), // Start date
                        max: demandSeries.length > 0 ? new Date(demandSeries[demandSeries.length - 1].datetime) : new Date(0), // End date
                        valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                      }]}
                      series={[
                        {
                          data: demandSeries.map((data) => parseInt(data.peakDemand)),
                          label: 'Peak Demand',
                          labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                          },
                          color: '#4681E1',
                          showMark: false
                        },
                        {
                          data: demandSeries.map((data) =>
                            Math.round(parseFloat(data.demand) * parseFloat(data.peakDemand))
                          ),
                          label: 'Forecasted Demand',
                          labelStyle: {
                            fontSize: '0.6vw',
                            fontFamily: 'Tahoma', // Example of adding fontFamily
                            letterSpacing: '0.1em',
                            color: '#333', // Example of adding color
                          },
                          color: '#545454',
                          showMark: false
                        }
                      ]}
                    />
            )}
            </Box>
            </Box>
            )}
            {selectedCharts.quantify_beta && (
            <Box className='graph'>
            <p className='Title'>Quantify Beta</p>
            <Box className='back'>
            {(!chartData || chartData.length === 0) ? (
                <p>No data available</p>
            ) : (
                <BarChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 1,
                            labelStyle: {
                                fontSize: '15px',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        }
                    }}
                    xAxis={[{
                        label: 'Datetime (YYYY-MM-DD)',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        data: chartData.map(item => new Date(item.datetime)),
                        min: new Date(chartData[0]?.datetime ?? new Date(0)),
                        max: new Date(chartData[chartData.length - 1]?.datetime ?? new Date(0)),
                        scaleType: 'band',
                        valueFormatter: (date) =>
                            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
                    }]}
                    yAxis={[{
                        label: 'Canadian Dollars ($)\n\n',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        }
                    }]}
                    series={[
                        {
                            data: chartData.map(item => Number(item.wisdm_revenue)),
                            label: 'Wisdm Revenue',
                            color: '#de3c64',
                            showMark: false,
                        },
                        {
                            data: chartData.map(item => Number(item.customer_revenue)),
                            label: 'Customer Revenue',
                            color: '#3c8dde',
                            showMark: false,
                        }
                        ]}
                        />
                        )}
                    </Box>
                    </Box>
                 )}
                {selectedCharts.sevenD_accuracy && (
                <Box className='graph'> {/*Small Forecast*/}
                <p className='Title'>Rolling 7 Day Accuracy</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80}}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                        fontSize: '10px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                        padding: 5,
                    },

                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    }
                }}
                xAxis={[{
                    id: 'date',
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    scaleType: 'time',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    data: datetimeAccuracy != [] ? [...datetimeAccuracy].reverse(): [],
                    min: datetimeAccuracy && datetimeAccuracy.length > 0 ? [...datetimeAccuracy].reverse()[0] : new Date(0),
                    max:
                        datetimeAccuracy && datetimeAccuracy.length > 0
                        ? [...datetimeAccuracy].reverse()[datetimeAccuracy.length - 1]
                    : new Date(0),
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                yAxis={[{ 
                    label:'Accuracy %\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                }]}
                series={[  
                { data: rollingAccuracy != [] ? rollingAccuracy:[], label: 'Accuracy', color: '#de3c64', showMark: true },
                { data: rollingBidAccuracy != [] ? rollingBidAccuracy:[], label: 'Bid Accuracy', color: '#3c75deff', showMark: true },
                ]}
                />
            </Box>  
            </Box>
            )}
            {selectedCharts.revenue && Array.isArray(revenueData) && revenueData.length > 0 && (
            <Box className='graph'> {/*Revenue*/}
                <p className='Title'>Revenue</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                    fontSize: '15px',
                    fontFamily: 'Tahoma', 
                    letterSpacing: '0.1em',
                    padding: 5,
                },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    },
                }}
                    xAxis={[{
                    
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: Array.isArray(revenueData) ? revenueData.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        }
                    }):[],
                    min: Array.isArray(revenueData) && revenueData.length > 0 ? new Date(revenueData[0]['datetime']) : new Date(0), // Start date
                    max: Array.isArray(revenueData) && revenueData.length > 0 ? new Date(revenueData[revenueData.length -  1]['datetime']) : new Date(0), // End date
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                   }]}
                    yAxis={[{ 
                    label:'Canadian Dollars ($)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    }]}
                    series={[
                    {
                        data: Array.isArray(revenueData) ? revenueData.map((data) => { 
                        let computedValue = Number(parseFloat(data['TNG'])* parseFloat(data['poolprice']))
                        return Math.max(computedValue, 0); }):[],
                        label: `Generation Revenue ${plantLabelUpper} (MW)`,
                        color: '#545454', 
                        showMark: false,
                        area: true,
                    },
                    ]}   
                />
            </Box>
            </Box>
            )}
           {selectedCharts.gross_profit && Array.isArray(revenueData) && revenueData.length > 0 && (
            <Box className='graph'> {/*Revenue*/}
                <p className='Title'>Gross Profit</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                    fontSize: '15px',
                    fontFamily: 'Tahoma', 
                    letterSpacing: '0.1em',
                    padding: 5,
                },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,
                    },
                }}
                    xAxis={[{
                    
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: Array.isArray(revenueData) ? revenueData.map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString['datetime']);
                            return date
                        }
                    }):[],
                    min: Array.isArray(revenueData) && revenueData.length > 0 ? new Date(revenueData[0]['datetime']) : new Date(0), // Start date
                    max: Array.isArray(revenueData) && revenueData.length > 0 ? new Date(revenueData[revenueData.length -  1]['datetime']) : new Date(0), // End date
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                   }]}
                    yAxis={[{ 
                    label:'Canadian Dollars ($)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    }]}
                    series={[
                    {
                        data: Array.isArray(revenueData) ? revenueData.map((data) => { 
                        let computedValue = Number(parseFloat(data['TNG'])* (parseFloat(data['poolprice']) - parseFloat(marginalCost)))
                        return Math.max(computedValue, 0); }):[],
                        label: `Gross Profit ${plantLabelUpper}`,
                        color: '#4681E1', 
                        showMark: false,
                        area: true,
                    },
                    ]}   
                />
            </Box>
            </Box>
            )}
            {selectedCharts.ST_supply && renewabledata != null && (
            <Box className='graph'> {/*Sort Term Supply*/}
                <p className='Title'>Short Term Supply</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                            padding: 5,
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    }
                }}
                yAxis={[{ 
                    label:'Forecasted Supply (MW)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em', 
                    }
                }]}
                xAxis={[
                    {
                    id: 'datetime',
                    data: renewabledata !== null ? renewabledata['time'].map(dateString => {
                        if (dateString) {
                        const date = new Date(dateString);
                        return date
                        } else {
                        return null;
                        }
                    }):[],
                    min: renewabledata !== null ? new Date(renewabledata['time'][0]): new Date(0), // Start date
                    max: renewabledata !== null ? new Date(renewabledata['time'][renewabledata['time'].length -  1]): new Date(0), // End date
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                    },
                ]}
                series={[
                    {
                        id: 'wind',
                        label: 'Wind',
                        data: renewabledata !== null ? renewabledata['wind'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: true,
                        color: '#545454'
                    },
                    {
                        id: 'solar',
                        label: 'Solar',
                        data: renewabledata !== null ?renewabledata['solar'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: true,
                        color: '#4681E1',
                    },
                ]}
                />
            </Box>
            </Box>
            )}
            {selectedCharts.LT_rockbash && rbdata != undefined && (
            <Box className='graph'> {/*Large Forecast*/}
                <p className='Title'>Long Term Forecast</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                            padding: 5,
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    },
                }}  
                yAxis={[{ 
                    label:'Forecasted Pool Price (CAD $)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '13px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                        lineHeight:"1.15em"
                    }
                }]}
                xAxis={[{
                    id: 'date',
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    data: rbdata !== null ? rbdata.map(dateString => {
                    if (dateString['datetime']) {
                        const date = new Date(dateString['datetime']);
                        return date
                    } else {
                        return null;
                    }
                    }):[],
                    min: rbdata !== null ? new Date(rbdata[0]['datetime']): new Date(0), // Start date
                    max: rbdata !== null ? new Date(rbdata[rbdata.length -  1]['datetime']): new Date(0), // End date
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                series={[
                    ...buildRockbashModelSeries(rbdata, false),
                ]}       
                />
            </Box>
            </Box>
            )}
            {selectedCharts.LT_supply && renewabledata != undefined && (
            <Box className='graph'> {/*Large Supply*/}
                <p className='Title'>Long Term Supply</p>
                <Box className='back'>
                <LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'middle' },
                        padding: 0,
                        labelStyle: {
                            fontSize: '15px',
                            padding: 5,
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                    }
                }}
                yAxis={[{ 
                    label:'Forecasted Supply (MW)\n\n',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15',
                        fontFamily: 'Tahoma', 
                        letterSpacing: '0.1em',
                    }
                }]}
                xAxis={[{
                    id: 'datetime',
                    data: renewabledata !== null ? renewabledata['time168'].map(dateString => {
                        if (dateString) {
                            const date = new Date(dateString);
                            return date
                        } else {
                            return null;
                        }
                    }):[],
                    min: renewabledata !== null ? new Date(renewabledata['time168'][0]): new Date(0), // Start date
                    max: renewabledata !== null ? new Date(renewabledata['time168'][renewabledata['time168'].length -  1]): new Date(0), // End date
                    scaleType: 'time',
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'Bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() +  1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                }]}
                series={[
                    {
                        id: 'wind',
                        label: 'Wind',
                        data: renewabledata !== null ? renewabledata['wind168'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: false,
                        color: '#545454'
                    },{
                        id: 'solar',
                        label: 'Solar',
                        data: renewabledata !== null ? renewabledata['solar168'].map(value => parseInt(value)):[],
                        stack: 'total',
                        area: true,
                        showMark: false,
                        color: '#4681E1',
                    }
                ]}
                />
            </Box>
            </Box>
            )}
            {selectedCharts.total_generation && pieChartData != [] && (
            <Box className='graph'> {/*Total Generation Pie*/}
                <p className='Title'>{name2}</p>
                <div>
                    <Button className='selectBox' aria-describedby={id2} variant="contained" onClick={handleClick2}>▼</Button>
                    <Popover
                        id={id2}
                        open={open2}
                        anchorEl={anchorEl2}
                        onClose={handleClose2}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <List component="div" aria-label="simple dropdown">
                        {List2.map((option, index) => (
                            <ListItemButton key={index} onClick={(e) => handleClick4(e, index)}>
                                <ListItemText primary={option} />
                            </ListItemButton>
                        ))}
                        </List>
                    </Popover>
                </div>
                <Box className='back'>
                {selectedItem2 === 0 ? (
                <PieChart
                slotProps={{
                    legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'left' },
                        padding: 10,
                        labelStyle: {
                            fontSize: '9px'
                        },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,   
                    }
                }}
                colors={['#6978A2', '#212121', '#545454','#4681E1','#3a3a3a','#2E8BC0','#274472','#189AB4']}
                series={[{
                    arcLabel: (item) => `${item.label} (${item.value})`,
                    cx: '60%',
                    cy: '60%',
                    arcLabelMinAngle: 60,
                    arcLabelRadius: "40%",
                    data: pieChartData,
                    innerRadius: '20%',
                    outerRadius: '70%',
                    paddingAngle: 2,
                    cornerRadius: 5
                }]}
                sx={{
                [`& .${pieArcLabelClasses.root}`]: {
                    fill: '#e7edf7',
                    fontWeight: 'bold',
                    fontSize: '10px',
                },
                }}
                />
            ):(series !== undefined && (<LineChart
                margin={{ left: 80 }}
                slotProps={{
                    legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                    labelStyle: {
                        fontSize: '12px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    itemMarkWidth: 10,
                    itemMarkHeight: 10,   
                    }
                }}
                yAxis={[{
                    label: 'Supply (MW)\n\n',
                    labelStyle: {
                    fontWeight: 'bold',
                    fontSize: '15px',
                    fontFamily: 'Tahoma',
                    letterSpacing: '0.1em',
                    },
                }]}
                xAxis={[
                    {
                    id: 'datetime',
                    data: xAxisData,
                    min: HCFdata !== undefined ? new Date(HCFdata[categories[0]][0]['datetime']): new Date(0),
                    max: HCFdata !== undefined ? new Date(HCFdata[categories[0]][HCFdata[categories[0]].length - 1]['datetime']): new Date(0),
                    label: 'Datetime (YYYY-MM-DD HH:mm)',
                    labelStyle: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        fontFamily: 'Tahoma',
                        letterSpacing: '0.1em',
                    },
                    scaleType: 'time',
                    valueFormatter: (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
                    },
                ]}
                colors={['#6978A2', '#212121', '#545454', '#4681E1', '#3a3a3a', '#2E8BC0', '#274472', '#189AB4']}
                series={series}
                />
            ))}
            </Box>
            </Box>
            )}
            {selectedCharts.market_offers_optimized && (
            <Box className='graph'>
            <p className='Title'>Optimized Market Offers Submitted</p>
            <Box className='back'>
                <BarChart
                    margin={{ left: 80 }}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'middle' },
                            padding: 1,
                            labelStyle: {
                                fontSize: '15px',
                                letterSpacing: '0.1em',
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                        }
                    }}
                    xAxis={[{
                        label: 'Datetime (YYYY-MM-DD)',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        data: bidChartSeriesPoints.map(point => point.time),
                        min: bidChartSeriesPoints[0]?.time ?? new Date(0),
                        max: bidChartSeriesPoints[bidChartSeriesPoints.length - 1]?.time ?? new Date(0),
                        scaleType: 'band',
                        valueFormatter: (date) =>
                        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ` +
                        `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
                    }]}
                    yAxis={[{
                        label: 'Canadian Dollars ($)\n\n',
                        labelStyle: {
                            fontWeight: 'Bold',
                            fontSize: '15px',
                            fontFamily: 'Tahoma',
                            letterSpacing: '0.1em',
                        },
                        min: Number(0),
                        max : Number(Math.max(...bidChartSeriesPoints.map(point => point.value), 1)),
                         tickMinStep: 1, 
                    }]}
                    series={[
                        {
                            data: bidChartSeriesPoints.map(point => point.value),
                            label: plantLabelUpper,
                            color: '#de3c64',
                            showMark: false,
                        }
                        ]}
                        />
                    </Box>
                    </Box>
                 )}
            </Box>
    );
}
